/**
 * useClimbLogs Hook
 *
 * Custom hook for querying climb logs from Firestore for the current user.
 * Supports filtering by date range and gym, with real-time updates.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  writeBatch,
  type DocumentData,
  type Unsubscribe,
  Timestamp,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { ClimbLog, ClimbOutcome, VGrade, RouteIdentification } from '@flash/shared';

/**
 * Convert Firestore document to ClimbLog type
 */
const docToClimbLog = (docData: DocumentData, docId: string): ClimbLog => {
  return {
    id: docId,
    userId: docData['userId'] as string,
    routeId: docData['routeId'] as string,
    gymId: docData['gymId'] as string,
    outcome: docData['outcome'] as ClimbOutcome,
    notes: (docData['notes'] as string | null) ?? null,
    loggedAt: docData['loggedAt'],
    createdAt: docData['createdAt'],
  };
};

/**
 * Route display data fetched along with climb logs
 */
export interface RouteDisplayData {
  /** Route name (may be empty) */
  name: string;
  /** Wall section where route is located */
  wallSection: string;
  /** Color grade name from gym */
  colorGrade: string;
  /** Official grade */
  officialGrade: VGrade | null;
  /** Route identification colors */
  identification?: RouteIdentification;
}

/**
 * Extended climb log with route information for display
 */
export interface ClimbLogWithRoute extends ClimbLog {
  /** Route name for display (legacy - prefer routeDisplayData) */
  routeName?: string;
  /** Route grade for display (legacy - prefer routeDisplayData) */
  routeGrade?: VGrade;
  /** Full route display data */
  routeDisplayData?: RouteDisplayData;
}

/**
 * Filter options for climb logs
 */
export interface ClimbLogFilters {
  /** Start date for filtering (inclusive) */
  startDate?: Date | undefined;
  /** End date for filtering (inclusive) */
  endDate?: Date | undefined;
  /** Filter by gym ID */
  gymId?: string | undefined;
}

/**
 * State interface for climb logs query
 */
interface UseClimbLogsState {
  /** List of climb logs with route info */
  logs: ClimbLogWithRoute[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Data for creating a new climb log
 */
export interface CreateClimbLogData {
  routeId: string;
  gymId: string;
  outcome: ClimbOutcome;
  notes?: string | null;
  loggedAt?: Date;
}

/**
 * Return type for useClimbLogs hook
 */
interface UseClimbLogsResult extends UseClimbLogsState {
  /** Current filters applied */
  filters: ClimbLogFilters;
  /** Update filters */
  setFilters: (filters: ClimbLogFilters) => void;
  /** Clear all filters */
  clearFilters: () => void;
  /** Add a new climb log */
  addClimbLog: (data: CreateClimbLogData) => Promise<string>;
  /** Delete a climb log */
  deleteClimbLog: (logId: string) => Promise<void>;
  /** Total count of logs */
  totalCount: number;
  /** Total sends count */
  sendsCount: number;
  /** Total attempts count */
  attemptsCount: number;
}

/**
 * Default filters
 */
const DEFAULT_FILTERS: ClimbLogFilters = {
  startDate: undefined,
  endDate: undefined,
  gymId: undefined,
};

/**
 * Hook to fetch and manage climb logs for the current user
 *
 * @param options - Optional initial filters
 * @returns Object containing climb logs, loading state, error, and management functions
 *
 * @example
 * ```tsx
 * const {
 *   logs,
 *   loading,
 *   error,
 *   filters,
 *   setFilters,
 *   addClimbLog,
 *   deleteClimbLog,
 *   sendsCount,
 *   attemptsCount,
 * } = useClimbLogs({
 *   initialFilters: { gymId: 'gym-123' },
 * });
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 *
 * return <ClimbHistoryList logs={logs} />;
 * ```
 */
export const useClimbLogs = (
  options: {
    initialFilters?: ClimbLogFilters;
  } = {}
): UseClimbLogsResult => {
  const { initialFilters = DEFAULT_FILTERS } = options;
  const { user } = useAuth();

  const [state, setState] = useState<UseClimbLogsState>({
    logs: [],
    loading: true,
    error: null,
  });

  const [filters, setFilters] = useState<ClimbLogFilters>(initialFilters);

  /**
   * Clear all filters to defaults
   */
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  /**
   * Add a new climb log and update route statistics
   */
  const addClimbLog = useCallback(
    async (data: CreateClimbLogData): Promise<string> => {
      if (!user) {
        throw new Error('Must be logged in to add climb log');
      }

      const db = getFirebaseDb();
      const batch = writeBatch(db);

      // Create the climb log document
      const logsRef = collection(db, 'climbLogs');
      const newLogRef = doc(logsRef);

      const logData = {
        userId: user.uid,
        routeId: data.routeId,
        gymId: data.gymId,
        outcome: data.outcome,
        notes: data.notes ?? null,
        loggedAt: data.loggedAt ? Timestamp.fromDate(data.loggedAt) : serverTimestamp(),
        createdAt: serverTimestamp(),
      };

      batch.set(newLogRef, logData);

      // Note: Route counter updates (totalSends/totalAttempts) are handled by
      // the onClimbLogWrite Firestore trigger in apps/functions/src/triggers/on-climb-log-write.ts
      // Do NOT add counter updates here - it will cause double-counting

      await batch.commit();
      return newLogRef.id;
    },
    [user]
  );

  /**
   * Delete a climb log
   *
   * Note: Route counter updates (totalSends/totalAttempts) are handled by
   * the onClimbLogWrite Firestore trigger in apps/functions/src/triggers/on-climb-log-write.ts
   */
  const deleteClimbLog = useCallback(
    async (logId: string): Promise<void> => {
      if (!user) {
        throw new Error('Must be logged in to delete climb log');
      }

      const db = getFirebaseDb();
      const logRef = doc(db, 'climbLogs', logId);

      await deleteDoc(logRef);
    },
    [user]
  );

  // Real-time subscription effect
  useEffect(() => {
    if (!user) {
      setState({ logs: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    let cancelled = false;
    const db = getFirebaseDb();
    const logsRef = collection(db, 'climbLogs');

    // Build query with userId filter and sort by loggedAt (newest first)
    let logsQuery = query(logsRef, where('userId', '==', user.uid), orderBy('loggedAt', 'desc'));

    // Add gymId filter if specified
    if (filters.gymId) {
      logsQuery = query(
        logsRef,
        where('userId', '==', user.uid),
        where('gymId', '==', filters.gymId),
        orderBy('loggedAt', 'desc')
      );
    }

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      logsQuery,
      async (snapshot) => {
        let logs: ClimbLog[] = snapshot.docs.map((docSnapshot) =>
          docToClimbLog(docSnapshot.data(), docSnapshot.id)
        );

        // Apply client-side date filtering if specified
        if (filters.startDate) {
          const startTime = filters.startDate.getTime();
          logs = logs.filter((log) => {
            const logTime = getLogTimestamp(log.loggedAt);
            return logTime >= startTime;
          });
        }

        if (filters.endDate) {
          // Add one day to include the end date fully
          const endTime = filters.endDate.getTime() + 24 * 60 * 60 * 1000;
          logs = logs.filter((log) => {
            const logTime = getLogTimestamp(log.loggedAt);
            return logTime < endTime;
          });
        }

        // Fetch route information for each unique routeId
        const uniqueRouteIds = [...new Set(logs.map((log) => log.routeId))];
        const routeDataMap = new Map<string, RouteDisplayData>();

        // Fetch all routes in parallel
        await Promise.all(
          uniqueRouteIds.map(async (routeId) => {
            try {
              const routeDoc = await getDoc(doc(db, 'routes', routeId));
              if (routeDoc.exists()) {
                const data = routeDoc.data();
                const identification = data['identification'] as RouteIdentification | undefined;
                const routeDisplayData: RouteDisplayData = {
                  name: (data['name'] as string) ?? '',
                  wallSection: (data['wallSection'] as string) ?? '',
                  colorGrade: (data['colorGrade'] as string) ?? '',
                  officialGrade: (data['officialGrade'] as VGrade | null) ?? null,
                  ...(identification && { identification }),
                };
                routeDataMap.set(routeId, routeDisplayData);
              }
            } catch {
              // Route not found or error fetching - leave as unknown
            }
          })
        );

        // Merge route info into logs
        const logsWithRoutes: ClimbLogWithRoute[] = logs.map((log) => {
          const routeDisplayData = routeDataMap.get(log.routeId);
          if (routeDisplayData) {
            // Keep legacy fields for backward compatibility
            const displayName = routeDisplayData.name || routeDisplayData.wallSection;
            const result: ClimbLogWithRoute = {
              ...log,
              routeName: displayName,
              routeDisplayData,
            };
            // Only add routeGrade if it exists
            if (routeDisplayData.officialGrade) {
              result.routeGrade = routeDisplayData.officialGrade;
            }
            return result;
          }
          return log;
        });

        if (!cancelled) {
          setState({ logs: logsWithRoutes, loading: false, error: null });
        }
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch climb logs';
        console.error('Error in climb logs subscription:', err);
        if (!cancelled) {
          setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
        }
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user, filters.gymId, filters.startDate, filters.endDate]);

  /**
   * Total count of logs
   */
  const totalCount = useMemo(() => state.logs.length, [state.logs]);

  /**
   * Count of sends
   */
  const sendsCount = useMemo(
    () => state.logs.filter((log) => log.outcome === 'send').length,
    [state.logs]
  );

  /**
   * Count of attempts
   */
  const attemptsCount = useMemo(
    () => state.logs.filter((log) => log.outcome === 'attempt').length,
    [state.logs]
  );

  return {
    ...state,
    filters,
    setFilters,
    clearFilters,
    addClimbLog,
    deleteClimbLog,
    totalCount,
    sendsCount,
    attemptsCount,
  };
};

/**
 * Get timestamp in milliseconds from a log's loggedAt field
 */
const getLogTimestamp = (loggedAt: unknown): number => {
  if (!loggedAt) return 0;

  // Handle Firestore Timestamp
  if (typeof loggedAt === 'object' && loggedAt !== null && 'toMillis' in loggedAt) {
    return (loggedAt as { toMillis: () => number }).toMillis();
  }

  // Handle Date object
  if (loggedAt instanceof Date) {
    return loggedAt.getTime();
  }

  // Handle timestamp with seconds/nanoseconds
  if (
    typeof loggedAt === 'object' &&
    loggedAt !== null &&
    'seconds' in loggedAt &&
    'nanoseconds' in loggedAt
  ) {
    const ts = loggedAt as { seconds: number; nanoseconds: number };
    return ts.seconds * 1000 + ts.nanoseconds / 1000000;
  }

  return 0;
};

export default useClimbLogs;
